import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import Button from '../../components/ui/Button'
import FormError from '../../components/ui/FormError'
import ProviderSettingsLayout from '../../components/provider/ProviderSettingsLayout'
import {
  MY_PROVIDER_MANAGEMENT_QUERY,
  MY_PROVIDER_PUBLIC_AVAILABILITY_QUERY,
  SET_AVAILABILITY_OVERRIDE_MUTATION,
  SET_WEEKLY_AVAILABILITY_MUTATION,
  type ManagedAvailabilityOverride,
  type ManagedAvailableSlot,
  type ManagedPublicAvailabilityData,
  type ManagedPublicAvailabilityVariables,
  type MyProviderManagementData,
  type SetAvailabilityOverrideData,
  type SetAvailabilityOverrideVariables,
  type SetWeeklyAvailabilityData,
  type SetWeeklyAvailabilityVariables,
} from '../../graphql/providerManagement'
import { useLang, useScopedTranslations } from '../../contexts/LangContext'

interface WeeklyDayState {
  dayOfWeek: number
  label: string
  active: boolean
  startTime: string
  endTime: string
}

interface OverrideFormState {
  date: string
  available: boolean
  startTime: string
  endTime: string
  reason: string
}

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const

const translations = {
  el: {
    title: 'Διαθεσιμότητα',
    body: 'Κράτησε ενημερωμένο το εβδομαδιαίο σου ωράριο και πρόσθεσε one-off εξαιρέσεις για συγκεκριμένες ημερομηνίες.',
    loading: 'Φορτώνουμε τη διαθεσιμότητα...',
    weeklyTitle: 'Εβδομαδιαίο πρόγραμμα',
    overrideTitle: 'Εξαίρεση ημερομηνίας',
    overrideBody: 'Πρόσθεσε μια συγκεκριμένη ημερομηνία με διαφορετικό ωράριο ή κλείσε την ημέρα.',
    note: 'Η επαναφορά ενός override στο βασικό εβδομαδιαίο πρόγραμμα δεν υποστηρίζεται ακόμη από το τρέχον API.',
    active: 'Ενεργό',
    start: 'Έναρξη',
    end: 'Λήξη',
    saveWeekly: 'Αποθήκευση εβδομαδιαίου προγράμματος',
    saveOverride: 'Αποθήκευση εξαίρεσης',
    savedWeekly: 'Το εβδομαδιαίο πρόγραμμα ενημερώθηκε.',
    savedOverride: 'Η εξαίρεση αποθηκεύτηκε.',
    date: 'Ημερομηνία',
    available: 'Διαθέσιμος εκείνη την ημέρα',
    reason: 'Σημείωση',
    reasonPlaceholder: 'Π.χ. Ειδικό ωράριο αργίας.',
    upcomingOverrides: 'Προσεχείς one-off εξαιρέσεις',
    none: 'Δεν υπάρχουν ακόμη καταχωρημένες εξαιρέσεις που να μπορούμε να προβάλλουμε.',
    closed: 'Μη διαθέσιμος',
    preloadWarning: 'Το τρέχον API δεν μπορεί ακόμη να ξαναφορτώσει υπάρχουσες εξαιρέσεις ή ωράρια για μη δημόσια προφίλ. Μια νέα αποθήκευση θα αντικαταστήσει το τρέχον πρόγραμμα.',
    day0: 'Κυριακή',
    day1: 'Δευτέρα',
    day2: 'Τρίτη',
    day3: 'Τετάρτη',
    day4: 'Πέμπτη',
    day5: 'Παρασκευή',
    day6: 'Σάββατο',
  },
  en: {
    title: 'Availability',
    body: 'Keep your weekly schedule current and add one-off exceptions for specific dates.',
    loading: 'Loading availability...',
    weeklyTitle: 'Weekly schedule',
    overrideTitle: 'Date override',
    overrideBody: 'Add a specific date with different hours or mark the day as unavailable.',
    note: 'Reverting an override back to the weekly schedule is not supported by the current API yet.',
    active: 'Active',
    start: 'Start',
    end: 'End',
    saveWeekly: 'Save weekly schedule',
    saveOverride: 'Save override',
    savedWeekly: 'Weekly schedule updated.',
    savedOverride: 'Override saved.',
    date: 'Date',
    available: 'Available on that date',
    reason: 'Note',
    reasonPlaceholder: 'E.g. Holiday schedule.',
    upcomingOverrides: 'Upcoming one-off overrides',
    none: 'No queryable overrides are available to show yet.',
    closed: 'Unavailable',
    preloadWarning: 'The current API cannot yet reload existing schedules or overrides for non-public profiles. A new save will replace the current schedule.',
    day0: 'Sunday',
    day1: 'Monday',
    day2: 'Tuesday',
    day3: 'Wednesday',
    day4: 'Thursday',
    day5: 'Friday',
    day6: 'Saturday',
  },
} as const

function todayIso() {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function addDaysIso(startDate: string, days: number) {
  const baseDate = new Date(`${startDate}T12:00:00`)
  baseDate.setDate(baseDate.getDate() + days)
  const year = baseDate.getFullYear()
  const month = String(baseDate.getMonth() + 1).padStart(2, '0')
  const day = String(baseDate.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function normalizeTime(timeValue: string | null) {
  return timeValue ? timeValue.slice(0, 5) : ''
}

export default function AvailabilityPage() {
  const { locale, t } = useLang()
  const copy = useScopedTranslations('provider.availability', translations[locale])
  const availabilityFrom = todayIso()
  const availabilityTo = addDaysIso(availabilityFrom, 60)

  const [weeklyDays, setWeeklyDays] = useState<WeeklyDayState[]>([])
  const [overrideForm, setOverrideForm] = useState<OverrideFormState>({
    date: availabilityFrom,
    available: true,
    startTime: '09:00',
    endTime: '17:00',
    reason: '',
  })
  const [localOverrides, setLocalOverrides] = useState<ManagedAvailabilityOverride[]>([])
  const [errors, setErrors] = useState<string[]>([])
  const [savedWeekly, setSavedWeekly] = useState(false)
  const [savedOverride, setSavedOverride] = useState(false)

  const { data: myData, loading: loadingProfile } = useQuery<MyProviderManagementData>(MY_PROVIDER_MANAGEMENT_QUERY)
  const providerId = myData?.myProviderProfile?.id

  const { data: publicData, loading: loadingPublic, refetch } = useQuery<ManagedPublicAvailabilityData, ManagedPublicAvailabilityVariables>(
    MY_PROVIDER_PUBLIC_AVAILABILITY_QUERY,
    {
      variables: {
        id: providerId ?? '',
        from: availabilityFrom,
        to: availabilityTo,
      },
      skip: !providerId,
    },
  )

  const [setWeeklyAvailability, { loading: savingWeekly }] = useMutation<SetWeeklyAvailabilityData, SetWeeklyAvailabilityVariables>(SET_WEEKLY_AVAILABILITY_MUTATION)
  const [setAvailabilityOverride, { loading: savingOverride }] = useMutation<SetAvailabilityOverrideData, SetAvailabilityOverrideVariables>(SET_AVAILABILITY_OVERRIDE_MUTATION)
  const publicProfileUnavailable = Boolean(providerId) && !loadingPublic && !publicData?.providerProfile

  useEffect(() => {
    const slots = publicData?.providerProfile?.availabilitySlots ?? []
    const nextDays = DAY_ORDER.map((dayOfWeek) => {
      const slot = slots.find((item) => item.dayOfWeek === dayOfWeek)
      return {
        dayOfWeek,
        label: copy[`day${dayOfWeek}`],
        active: Boolean(slot),
        startTime: normalizeTime(slot?.startTime ?? null) || '09:00',
        endTime: normalizeTime(slot?.endTime ?? null) || '17:00',
      }
    })
    setWeeklyDays(nextDays)
  }, [copy, publicData])

  const queryableOverrides = useMemo(() => {
    const availableSlots = publicData?.providerProfile?.availableSlots ?? []
    return availableSlots
      .filter((slot) => slot.source === 'override')
      .map((slot: ManagedAvailableSlot) => ({
        id: slot.date,
        date: slot.date,
        available: true,
        startTime: slot.startTime,
        endTime: slot.endTime,
        reason: null,
      }))
  }, [publicData])

  const allOverrides = useMemo(() => {
    const map = new Map<string, ManagedAvailabilityOverride>()
    queryableOverrides.forEach((override) => map.set(override.date, override))
    localOverrides.forEach((override) => map.set(override.date, override))
    return Array.from(map.values()).sort((left, right) => left.date.localeCompare(right.date))
  }, [localOverrides, queryableOverrides])

  async function handleSaveWeekly() {
    setErrors([])
    setSavedWeekly(false)
    setSavedOverride(false)

    try {
      const { data } = await setWeeklyAvailability({
        variables: {
          slots: weeklyDays
            .filter((day) => day.active)
            .map((day) => ({
              dayOfWeek: day.dayOfWeek,
              startTime: day.startTime,
              endTime: day.endTime,
            })),
        },
      })

      const mutationErrors = data?.setWeeklyAvailability.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save availability.')]
      if (mutationErrors.length > 0) {
        setErrors(mutationErrors)
        return
      }

      setSavedWeekly(true)
      await refetch()
    } catch {
      setErrors([t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save availability.')])
    }
  }

  async function handleSaveOverride() {
    setErrors([])
    setSavedWeekly(false)
    setSavedOverride(false)

    try {
      const { data } = await setAvailabilityOverride({
        variables: {
          date: overrideForm.date,
          available: overrideForm.available,
          startTime: overrideForm.available ? overrideForm.startTime : undefined,
          endTime: overrideForm.available ? overrideForm.endTime : undefined,
          reason: overrideForm.reason.trim() || undefined,
        },
      })

      const mutationErrors = data?.setAvailabilityOverride.errors ?? [t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save override.')]
      if (mutationErrors.length > 0) {
        setErrors(mutationErrors)
        return
      }

      const override = data?.setAvailabilityOverride.override
      if (override) {
        setLocalOverrides((current) => {
          const next = current.filter((item) => item.date !== override.date)
          return [...next, override]
        })
      }
      setSavedOverride(true)
      await refetch()
    } catch {
      setErrors([t('Δεν ήταν δυνατή η αποθήκευση.', 'Could not save override.')])
    }
  }

  return (
    <ProviderSettingsLayout title={copy.title} body={copy.body}>
      <section className="space-y-8">
        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          {loadingProfile || loadingPublic ? (
            <p className="text-sm text-slate-500">{copy.loading}</p>
          ) : (
            <>
              <FormError errors={errors} />
              {publicProfileUnavailable && (
                <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                  {copy.preloadWarning}
                </div>
              )}
              {savedWeekly && (
                <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                  {copy.savedWeekly}
                </div>
              )}
              <h2 className="text-2xl font-bold text-slate-950">{copy.weeklyTitle}</h2>
              <div className="mt-6 space-y-3">
                {weeklyDays.map((day) => (
                  <article key={day.dayOfWeek} className="grid gap-4 rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4 md:grid-cols-[160px_110px_1fr_1fr] md:items-center">
                    <p className="font-semibold text-slate-950">{day.label}</p>
                    <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
                      <input
                        type="checkbox"
                        checked={day.active}
                        onChange={(event) => {
                          setSavedWeekly(false)
                          setWeeklyDays((current) => current.map((item) => item.dayOfWeek === day.dayOfWeek ? { ...item, active: event.target.checked } : item))
                        }}
                      />
                      {copy.active}
                    </label>
                    <label className="text-sm font-medium text-slate-700">
                      <span className="mb-2 block">{copy.start}</span>
                      <input
                        type="time"
                        value={day.startTime}
                        disabled={!day.active}
                        onChange={(event) => {
                          setSavedWeekly(false)
                          setWeeklyDays((current) => current.map((item) => item.dayOfWeek === day.dayOfWeek ? { ...item, startTime: event.target.value } : item))
                        }}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 disabled:bg-slate-100"
                      />
                    </label>
                    <label className="text-sm font-medium text-slate-700">
                      <span className="mb-2 block">{copy.end}</span>
                      <input
                        type="time"
                        value={day.endTime}
                        disabled={!day.active}
                        onChange={(event) => {
                          setSavedWeekly(false)
                          setWeeklyDays((current) => current.map((item) => item.dayOfWeek === day.dayOfWeek ? { ...item, endTime: event.target.value } : item))
                        }}
                        className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 disabled:bg-slate-100"
                      />
                    </label>
                  </article>
                ))}
              </div>

              <div className="mt-8 flex justify-end">
                <Button type="button" size="lg" loading={savingWeekly} onClick={handleSaveWeekly}>
                  {copy.saveWeekly}
                </Button>
              </div>
            </>
          )}
        </div>

        <div className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
          {savedOverride && (
            <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
              {copy.savedOverride}
            </div>
          )}
          {publicProfileUnavailable && (
            <div className="mb-4 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
              {copy.preloadWarning}
            </div>
          )}
          <h2 className="text-2xl font-bold text-slate-950">{copy.overrideTitle}</h2>
          <p className="mt-3 text-sm leading-7 text-slate-600">{copy.overrideBody}</p>
          <p className="mt-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">{copy.note}</p>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-slate-700">
              <span className="mb-2 block">{copy.date}</span>
              <input
                type="date"
                value={overrideForm.date}
                min={availabilityFrom}
                onChange={(event) => {
                  setSavedOverride(false)
                  setOverrideForm((current) => ({ ...current, date: event.target.value }))
                }}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
              />
            </label>

            <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={overrideForm.available}
                onChange={(event) => {
                  setSavedOverride(false)
                  setOverrideForm((current) => ({ ...current, available: event.target.checked }))
                }}
              />
              {copy.available}
            </label>

            <label className="text-sm font-medium text-slate-700">
              <span className="mb-2 block">{copy.start}</span>
              <input
                type="time"
                value={overrideForm.startTime}
                disabled={!overrideForm.available}
                onChange={(event) => {
                  setSavedOverride(false)
                  setOverrideForm((current) => ({ ...current, startTime: event.target.value }))
                }}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 disabled:bg-slate-100"
              />
            </label>

            <label className="text-sm font-medium text-slate-700">
              <span className="mb-2 block">{copy.end}</span>
              <input
                type="time"
                value={overrideForm.endTime}
                disabled={!overrideForm.available}
                onChange={(event) => {
                  setSavedOverride(false)
                  setOverrideForm((current) => ({ ...current, endTime: event.target.value }))
                }}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 disabled:bg-slate-100"
              />
            </label>

            <label className="text-sm font-medium text-slate-700 md:col-span-2">
              <span className="mb-2 block">{copy.reason}</span>
              <textarea
                value={overrideForm.reason}
                onChange={(event) => {
                  setSavedOverride(false)
                  setOverrideForm((current) => ({ ...current, reason: event.target.value }))
                }}
                rows={3}
                placeholder={copy.reasonPlaceholder}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
              />
            </label>
          </div>

          <div className="mt-6 flex justify-end">
            <Button type="button" loading={savingOverride} onClick={handleSaveOverride}>
              {copy.saveOverride}
            </Button>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-bold text-slate-950">{copy.upcomingOverrides}</h3>
            <div className="mt-4 space-y-3">
              {allOverrides.length === 0 ? (
                <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                  {copy.none}
                </div>
              ) : (
                allOverrides.map((override) => (
                  <article key={override.date} className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-950">{override.date}</p>
                        <p className="mt-1 text-sm text-slate-600">
                          {override.available
                            ? `${normalizeTime(override.startTime)} - ${normalizeTime(override.endTime)}`
                            : copy.closed}
                        </p>
                      </div>
                      {override.reason && (
                        <p className="text-sm text-slate-500">{override.reason}</p>
                      )}
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>
        </div>
      </section>
    </ProviderSettingsLayout>
  )
}
